<template>
  <div>
    <b-row class="mb-3">
      <b-col>
        <h5>Webhook Subscriptions</h5>
      </b-col>
      <b-col cols="auto">
        <b-button variant="primary" size="sm" @click="openCreateModal">
          <i class="fa fa-plus mr-1" />
          Add Webhook
        </b-button>
      </b-col>
    </b-row>

    <b-table
      :items="webhooks"
      :fields="fields"
      :busy="loading"
      responsive
      show-empty
    >
      <template #cell(event_type)="{ item }">
        <b-badge variant="info">{{ item.event_type }}</b-badge>
      </template>

      <template #cell(is_active)="{ item }">
        <b-badge :variant="item.is_active ? 'success' : 'secondary'">
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </b-badge>
      </template>

      <template #cell(http_method)="{ item }">
        <code>{{ item.http_method }}</code>
      </template>

      <template #cell(actions)="{ item }">
        <b-button size="sm" variant="outline-info" class="mr-1" :disabled="testingId === item.id" @click="testWebhook(item)">
          <b-spinner v-if="testingId === item.id" small />
          <i v-else class="fa fa-bolt" />
        </b-button>
        <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEditModal(item)">
          <i class="fa fa-edit" />
        </b-button>
        <b-button size="sm" variant="outline-danger" @click="confirmDelete(item)">
          <i class="fa fa-trash" />
        </b-button>
      </template>

      <template #table-busy>
        <div class="text-center my-3"><b-spinner variant="primary" /></div>
      </template>
    </b-table>

    <!-- Create/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="editingWebhook ? 'Edit Webhook' : 'Create Webhook'"
      size="lg"
      @ok="saveWebhook"
      :ok-disabled="!webhookForm.event_type"
    >
      <b-row>
        <b-col md="6">
          <b-form-group label="Event Type *">
            <b-form-select v-model="webhookForm.event_type" :options="eventOptions">
              <template #first>
                <b-form-select-option :value="''">Select an event...</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="HTTP Method">
            <b-form-select v-model="webhookForm.http_method" :options="httpMethods" />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group label="Target Endpoint">
            <b-form-input v-model="webhookForm.target_endpoint" placeholder="https://example.com/webhook" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Retry Count">
            <b-form-input v-model.number="webhookForm.retry_count" type="number" min="0" max="10" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Retry Interval (seconds)">
            <b-form-input v-model.number="webhookForm.retry_interval_seconds" type="number" min="60" max="3600" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Active">
            <b-form-checkbox v-model="webhookForm.is_active" switch />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Delete Confirm -->
    <b-modal v-model="showDeleteModal" title="Confirm Delete" @ok="deleteWebhook">
      <p>Are you sure you want to delete this webhook?</p>
    </b-modal>

    <!-- Test Result -->
    <b-modal v-model="showTestResult" title="Webhook Test Result" ok-only>
      <div v-if="testResult">
        <b-alert :variant="testResult.success ? 'success' : 'danger'" show>
          {{ testResult.message || (testResult.success ? 'Delivered successfully' : 'Delivery failed') }}
        </b-alert>
        <div v-if="testResult.response_code">
          <strong>Response Code:</strong> {{ testResult.response_code }}
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  props: {
    connectorId: { type: Number, required: true },
  },
  data() {
    return {
      loading: false,
      showModal: false,
      showDeleteModal: false,
      showTestResult: false,
      editingWebhook: null,
      selectedWebhook: null,
      testingId: null,
      testResult: null,
      webhookForm: {
        event_type: '',
        http_method: 'POST',
        target_endpoint: '',
        is_active: true,
        retry_count: 3,
        retry_interval_seconds: 300,
        custom_headers: null,
        payload_template: null,
      },
      fields: [
        { key: 'event_type', label: 'Event' },
        { key: 'target_endpoint', label: 'Endpoint' },
        { key: 'http_method', label: 'Method' },
        { key: 'is_active', label: 'Status' },
        { key: 'actions', label: '', class: 'text-right' },
      ],
      httpMethods: [
        { value: 'GET', text: 'GET' },
        { value: 'POST', text: 'POST' },
        { value: 'PUT', text: 'PUT' },
        { value: 'PATCH', text: 'PATCH' },
        { value: 'DELETE', text: 'DELETE' },
      ],
    }
  },
  computed: {
    webhooks() {
      return this.$store.state.appConnector.webhooks
    },
    eventOptions() {
      const events = this.$store.state.appConnector.availableEvents || {}
      if (Array.isArray(events)) {
        return events.map(e => {
          if (typeof e === 'string') return { value: e, text: e }
          return { value: e.event_type || e.value, text: e.label || e.event_type || e.value }
        })
      }
      return Object.entries(events).map(([key, desc]) => ({
        value: key,
        text: `${key} — ${desc}`,
      }))
    },
  },
  async mounted() {
    this.loading = true
    try {
      await Promise.all([
        this.$store.dispatch('appConnector/fetchWebhooks', this.connectorId),
        this.$store.dispatch('appConnector/fetchAvailableEvents'),
      ])
    } finally {
      this.loading = false
    }
  },
  methods: {
    openCreateModal() {
      this.editingWebhook = null
      this.webhookForm = {
        event_type: '',
        http_method: 'POST',
        target_endpoint: '',
        is_active: true,
        retry_count: 3,
        retry_interval_seconds: 300,
        custom_headers: null,
        payload_template: null,
      }
      this.showModal = true
    },
    openEditModal(webhook) {
      this.editingWebhook = webhook
      this.webhookForm = {
        event_type: webhook.event_type,
        http_method: webhook.http_method || 'POST',
        target_endpoint: webhook.target_endpoint || '',
        is_active: webhook.is_active,
        retry_count: webhook.retry_count || 3,
        retry_interval_seconds: webhook.retry_interval_seconds || 300,
        custom_headers: webhook.custom_headers,
        payload_template: webhook.payload_template,
      }
      this.showModal = true
    },
    async saveWebhook() {
      try {
        if (this.editingWebhook) {
          await this.$store.dispatch('appConnector/updateWebhook', {
            connectorId: this.connectorId,
            webhookId: this.editingWebhook.id,
            data: this.webhookForm,
          })
        } else {
          await this.$store.dispatch('appConnector/createWebhook', {
            connectorId: this.connectorId,
            data: this.webhookForm,
          })
        }
        this.showModal = false
      } catch (err) {
        // handled in store
      }
    },
    confirmDelete(webhook) {
      this.selectedWebhook = webhook
      this.showDeleteModal = true
    },
    async deleteWebhook() {
      if (this.selectedWebhook) {
        await this.$store.dispatch('appConnector/deleteWebhook', {
          connectorId: this.connectorId,
          webhookId: this.selectedWebhook.id,
        })
        this.showDeleteModal = false
        this.selectedWebhook = null
      }
    },
    async testWebhook(webhook) {
      this.testingId = webhook.id
      try {
        this.testResult = await this.$store.dispatch('appConnector/testWebhook', {
          connectorId: this.connectorId,
          webhookId: webhook.id,
        })
        this.showTestResult = true
      } catch (err) {
        this.testResult = { success: false, message: 'Test failed' }
        this.showTestResult = true
      } finally {
        this.testingId = null
      }
    },
  },
}
</script>
